import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import * as React from "react";
import { Check, ChevronDown, CircleX } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandInput,
  CommandEmpty,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { buttonVariants } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

type Option = {
  label: string;
  value: string;
  soon?: boolean;
  icon?: React.ReactElement;
};

interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  showSearch?: boolean;
  isAllSelected?: React.ReactElement | null;
  className?: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
  disabled?: boolean;
  showIcon?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  isAllSelected = null,
  showSearch = true,
  className = "",
  showIcon,
  badge,
  disabled = false,
  children,
  placeholder = "Select options...",
}: MultiSelectProps) {
  const { t } = useLocale();
  const [open, setOpen] = React.useState(false);
  const isAllSelectedValues =
    selected.length === options.filter((opt) => !opt.soon).length;
  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (!disabled) {
      setOpen(value);
    }
  };

  const handleSelectAll = () => {
    if (isAllSelectedValues || selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.filter((opt) => !opt.soon).map((opt) => opt.value));
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-expanded={open}
          disabled={disabled}
          role="combobox"
          className={cn(
            buttonVariants({ variant: "outline" }),
            "w-fit justify-between cursor-pointer",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
        >
          {selected.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {(selected.length <= 1 ? selected : selected.slice(0, 1)).map(
                (value) => {
                  const option = options.find((opt) => opt.value === value);
                  return (
                    <Badge
                      key={value}
                      variant="secondary"
                      onClick={() => toggleOption(value)}
                    >
                      {showIcon && option?.icon}
                      {option?.label}
                      <CircleX />
                    </Badge>
                  );
                },
              )}
              {selected.length > 1 && (
                <Badge variant="secondary">+{selected.length - 1}</Badge>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-center gap-1">
              <div className="flex items-center gap-1 text-[#0A0A0A]">
                {children}
                {placeholder}
              </div>
              {badge ? badge : null}
            </div>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      {!disabled && (
        <PopoverContent className="w-[200px] p-0">
          <Command>
            {showSearch && (
              <CommandInput placeholder={t("common.search")} />
            )}
            <CommandList>
              <CommandEmpty>
                <LocalizedText id="legacy.no.options.found.q3um4s" />
              </CommandEmpty>
              <CommandGroup>
                {isAllSelected && (
                  <CommandItem onSelect={handleSelectAll}>
                    <div className="ml-2 flex justify-between w-full cursor-pointer items-center">
                      {isAllSelected}
                      {isAllSelectedValues && <Check className="h-4 w-4" />}
                    </div>
                  </CommandItem>
                )}
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (!option.soon) toggleOption(option.value);
                    }}
                    className={`
                      ${
                        selected.includes(option.value) &&
                        "data-[selected=true]:bg-[#FFE2CC] aria-selected:bg-[#FFE2CC] hover:bg-[#FFE2CC] bg-[#FFE2CC]"
                      }`}
                  >
                    {option.soon && (
                      <div className="absolute inset-0 bg-white/40"></div>
                    )}
                    <div className="ml-2 flex justify-between w-full cursor-pointer items-center">
                      <div className="flex items-center gap-2">
                        {option.icon && option.icon}
                        {option.label}
                      </div>
                      {selected.includes(option.value) && !option?.soon && (
                        <Check className="h-4 w-4" />
                      )}
                      {option?.soon && <Badge variant="secondary">soon</Badge>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
}
